import React, {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Table } from 'react-bootstrap';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';
import { formatCurrency, formatVariancePercent } from './formatters';

const SUB_COLUMNS = ['Benefit', 'Budget', 'Variance', '%Variance'];

const PeriodFigureCells = ({ figure, cellStyle, onClick }) => {
  const clickableStyle = onClick
    ? { ...cellStyle, cursor: 'pointer' }
    : cellStyle;
  return (
    <>
      <td className="text-end" style={clickableStyle} onClick={onClick}>
        {formatCurrency(figure?.profitAndLoss)}
      </td>
      <td className="text-end" style={cellStyle}>
        {formatCurrency(figure?.budget)}
      </td>
      <td className="text-end" style={cellStyle}>
        {formatCurrency(figure?.variance)}
      </td>
      <td className="text-end" style={cellStyle}>
        {formatVariancePercent(figure?.variancePercent)}
      </td>
    </>
  );
};

PeriodFigureCells.propTypes = {
  figure: PropTypes.shape({
    profitAndLoss: PropTypes.number,
    budget: PropTypes.number,
    variance: PropTypes.number,
    variancePercent: PropTypes.number
  }),
  cellStyle: PropTypes.object.isRequired,
  onClick: PropTypes.func
};

const DepartmentSpendingSummaryTable = ({ summary, onCellClick }) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const borderColor = isDark ? '#4a4f58' : '#c4c9d0';

  const headerRow1Ref = useRef(null);
  const [headerRow1Height, setHeaderRow1Height] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (headerRow1Ref.current) {
        setHeaderRow1Height(
          headerRow1Ref.current.getBoundingClientRect().height
        );
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [summary, isDark]);

  const headerCellStyle = useMemo(
    () => ({
      backgroundColor: isDark ? 'rgb(34 45 59)' : '#d8e2ef',
      color: isDark ? 'white' : '#000000',
      fontWeight: 'bold',
      fontSize: '0.75rem',
      border: `1px solid ${borderColor}`,
      padding: '8px 12px',
      verticalAlign: 'middle'
    }),
    [isDark, borderColor]
  );

  const headerRow1CellStyle = useMemo(
    () => ({ ...headerCellStyle, position: 'sticky', top: 0, zIndex: 3 }),
    [headerCellStyle]
  );

  const headerRow2CellStyle = useMemo(
    () => ({
      ...headerCellStyle,
      position: 'sticky',
      top: headerRow1Height,
      zIndex: 2
    }),
    [headerCellStyle, headerRow1Height]
  );

  const bodyCellStyle = useMemo(
    () => ({
      border: `1px solid ${borderColor}`,
      padding: '8px 10px',
      fontSize: '0.8rem',
      color: isDark ? 'white' : 'black'
    }),
    [isDark, borderColor]
  );

  const stripeColors = useMemo(
    () => ({
      even: isDark ? '#1e1e2f' : '#ffffff',
      odd: isDark ? '#262838' : 'aliceblue'
    }),
    [isDark]
  );

  const totalRowStyle = useMemo(
    () => ({
      ...bodyCellStyle,
      fontWeight: 'bold',
      backgroundColor: isDark ? '#2a3547' : '#eef3f9'
    }),
    [bodyCellStyle, isDark]
  );

  const periodLabelsNewestFirst = useMemo(
    () => (summary?.periodLabels ? [...summary.periodLabels].reverse() : []),
    [summary]
  );

  const periodIdsNewestFirst = useMemo(
    () => (summary?.periodIds ? [...summary.periodIds].reverse() : []),
    [summary]
  );

  const toNewestFirst = periods => (periods ? [...periods].reverse() : []);

  if (!summary) return null;

  const { totalRow, rows } = summary;

  const renderPeriodGroups = (row, rowStyle) => (
    <>
      <PeriodFigureCells
        figure={row.total}
        cellStyle={rowStyle}
        onClick={
          onCellClick
            ? () => onCellClick({ account: row, periodId: undefined })
            : undefined
        }
      />
      {toNewestFirst(row.periods).map((figure, columnIndex) => (
        <PeriodFigureCells
          key={columnIndex}
          figure={figure}
          cellStyle={rowStyle}
          onClick={
            onCellClick
              ? () =>
                  onCellClick({
                    account: row,
                    periodId: periodIdsNewestFirst[columnIndex],
                    periodLabel: periodLabelsNewestFirst[columnIndex]
                  })
              : undefined
          }
        />
      ))}
    </>
  );

  return (
    <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
      <Table size="sm" className="mb-0">
        <thead>
          <tr ref={headerRow1Ref}>
            <th rowSpan={2} style={headerRow1CellStyle}>
              Code
            </th>
            <th rowSpan={2} style={headerRow1CellStyle}>
              Name
            </th>
            <th colSpan={4} className="text-center" style={headerRow1CellStyle}>
              Total
            </th>
            {periodLabelsNewestFirst.map(label => (
              <th
                key={label}
                colSpan={4}
                className="text-center"
                style={headerRow1CellStyle}
              >
                {label}
              </th>
            ))}
          </tr>
          <tr>
            {Array.from({ length: periodLabelsNewestFirst.length + 1 }).map(
              (_, groupIdx) =>
                SUB_COLUMNS.map(col => (
                  <th
                    key={`${groupIdx}-${col}`}
                    className="text-center"
                    style={headerRow2CellStyle}
                  >
                    {col}
                  </th>
                ))
            )}
          </tr>
        </thead>
        <tbody>
          {totalRow && (
            <tr>
              <td style={totalRowStyle}>{totalRow.code}</td>
              <td style={totalRowStyle}>{totalRow.name}</td>
              {renderPeriodGroups(totalRow, totalRowStyle)}
            </tr>
          )}
          {(rows || []).map((row, rowIdx) => {
            const rowStyle = {
              ...bodyCellStyle,
              backgroundColor:
                rowIdx % 2 === 0 ? stripeColors.even : stripeColors.odd
            };
            return (
              <tr key={row.code}>
                <td style={rowStyle}>{row.code}</td>
                <td style={rowStyle}>{row.name}</td>
                {renderPeriodGroups(row, rowStyle)}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

DepartmentSpendingSummaryTable.propTypes = {
  summary: PropTypes.shape({
    periodLabels: PropTypes.arrayOf(PropTypes.string),
    periodIds: PropTypes.arrayOf(PropTypes.number),
    totalRow: PropTypes.object,
    rows: PropTypes.arrayOf(PropTypes.object)
  }),
  onCellClick: PropTypes.func
};

export default DepartmentSpendingSummaryTable;
